import {
  CheckOutlined,
  DesktopOutlined,
  DownOutlined,
  KeyOutlined,
  LogoutOutlined,
  MenuOutlined,
  MoonOutlined,
  SunOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Avatar,
  Button,
  Drawer,
  Dropdown,
  Flex,
  Layout,
  Menu,
  Switch,
  Tag,
  Typography,
  theme,
} from 'antd';
import type { MenuProps } from 'antd';
import { Suspense, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';

import { menuItemsFor, type NavItem } from '@/app/navigation';
import { FullScreenSpin } from '@/components/FullScreenSpin';
import { PoweredBy } from '@/components/PoweredBy';
import { SystemBanners } from '@/components/SystemBanners';
import { displayName, useCurrentUser } from '@/features/auth/authContext';
import type { Role } from '@/features/auth/authContext';
import { RoleSwitcher } from '@/features/auth/RoleSwitcher';
import { SessionsModal } from '@/features/auth/SessionsModal';
import { useRoleSwitch } from '@/features/auth/useRoleSwitch';
import { useAppConfig } from '@/features/config/configContext';
import { useLogoutRequest } from '@/features/shifts/operatorShiftContext';
import { ChangePasswordModal } from '@/features/users/PasswordForm';
import { ru } from '@/i18n/ru';
import { useIsMobile } from '@/lib/useIsMobile';
import { useThemeMode } from '@/theme/themeContext';

type MenuItems = NonNullable<MenuProps['items']>;

function menuItems(items: readonly NavItem[]): MenuItems {
  return items.map(({ path, label, icon: Icon, children }) => {
    if (!children) return { key: path, label, icon: <Icon /> };
    const groups = [...new Set(children.map((child) => child.group))];
    return {
      key: path,
      label,
      icon: <Icon />,
      children: [
        { key: path, label: ru.dict.overviewLink },
        ...groups.map((group) => ({
          type: 'group' as const,
          key: `${path}#${group}`,
          label: ru.dict.groups[group],
          children: children
            .filter((child) => child.group === group)
            .map((child) => ({ key: child.path, label: child.label })),
        })),
      ],
    };
  });
}

/** Longest menu path that is a prefix of the current location. */
function activeKey(items: readonly NavItem[], pathname: string): string | undefined {
  const paths = items.flatMap((item) => [item.path, ...(item.children ?? []).map((c) => c.path)]);
  return paths
    .filter((path) => pathname === path || pathname.startsWith(`${path}/`))
    .sort((a, b) => b.length - a.length)[0];
}

/** The label of the section on screen: a dictionary's own, else its section's. */
function sectionLabel(items: readonly NavItem[], key: string | undefined): string | undefined {
  for (const item of items) {
    const child = item.children?.find((c) => c.path === key);
    if (child) return child.label;
    if (item.path === key) return item.label;
  }
  return undefined;
}

type UserAction = 'password' | 'sessions' | 'logout';
const ROLE_KEY = 'role:';
const THEME_KEY = 'theme';

/** The phone's header menu (STAGE_7, mobile): everything the desktop header shows side by
 * side - who is signed in, the mode, the theme - and the account's own actions. */
function MobileUserMenu({ onAction }: { onAction: (action: UserAction) => void }) {
  const user = useCurrentUser();
  const { mode, toggle } = useThemeMode();
  const { roles, active, canSwitch, switchTo } = useRoleSwitch();
  const items: MenuItems = [
    {
      key: 'who',
      disabled: true,
      label: (
        <Flex vertical>
          <Typography.Text strong>{displayName(user)}</Typography.Text>
          <Typography.Text type="secondary">{ru.roles[user.role]}</Typography.Text>
        </Flex>
      ),
    },
    { type: 'divider' },
    ...(canSwitch
      ? [
          {
            type: 'group' as const,
            key: 'modes',
            label: ru.header.mode,
            children: roles.map((role) => ({
              key: `${ROLE_KEY}${role}`,
              label: ru.roles[role],
              icon: role === active ? <CheckOutlined /> : <span style={{ width: 14 }} />,
            })),
          },
          { type: 'divider' as const },
        ]
      : []),
    {
      key: THEME_KEY,
      icon: mode === 'dark' ? <MoonOutlined /> : <SunOutlined />,
      label: (
        <Flex align="center" justify="space-between" gap={12}>
          {ru.header.darkTheme}
          <Switch size="small" checked={mode === 'dark'} aria-label={ru.header.darkTheme} />
        </Flex>
      ),
    },
    { key: 'sessions', label: ru.header.sessions, icon: <DesktopOutlined /> },
    { key: 'password', label: ru.header.changePassword, icon: <KeyOutlined /> },
    { key: 'logout', label: ru.header.logout, icon: <LogoutOutlined /> },
  ];
  return (
    <Dropdown
      trigger={['click']}
      placement="bottomRight"
      menu={{
        items,
        onClick: ({ key }) => {
          if (key === THEME_KEY) toggle();
          else if (key.startsWith(ROLE_KEY)) void switchTo(key.slice(ROLE_KEY.length) as Role);
          else onAction(key as UserAction);
        },
      }}
    >
      <Button type="text" shape="circle" aria-label={ru.header.userMenu} style={{ flex: 'none' }}>
        <Avatar size="small" icon={<UserOutlined />} />
      </Button>
    </Dropdown>
  );
}

/** Web workspace shell for admin and operator: role menu, header, page outlet. On a
 * phone the menu is a drawer over the page and the header one row (STAGE_7, mobile). */
export function AppLayout() {
  const user = useCurrentUser();
  const logout = useLogoutRequest();
  const config = useAppConfig();
  const [changingPassword, setChangingPassword] = useState(false);
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const { mode, toggle } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = theme.useToken();
  const mobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const items = menuItemsFor(user.role);
  const selected = activeKey(items, location.pathname);
  const openSection = items.find((item) => item.children && selected?.startsWith(item.path));

  const onAction = (action: UserAction) => {
    if (action === 'password') setChangingPassword(true);
    else if (action === 'sessions') setSessionsOpen(true);
    else logout();
  };
  const navigation = (
    <Menu
      mode="inline"
      aria-label={ru.nav.menu}
      selectedKeys={selected ? [selected] : []}
      defaultOpenKeys={openSection ? [openSection.path] : []}
      items={menuItems(items)}
      onClick={({ key }) => {
        // On a phone the drawer gets out of the way of the page it opened.
        setDrawerOpen(false);
        void navigate(key);
      }}
      style={{ borderInlineEnd: 'none' }}
    />
  );
  const modals = (
    <>
      <SessionsModal
        open={sessionsOpen}
        onClose={() => {
          setSessionsOpen(false);
        }}
      />
      <ChangePasswordModal
        open={changingPassword}
        onClose={() => {
          setChangingPassword(false);
        }}
      />
    </>
  );

  if (mobile) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Drawer
          placement="left"
          width={280}
          open={drawerOpen}
          title={config.org_name || config.product_name}
          onClose={() => {
            setDrawerOpen(false);
          }}
          styles={{ body: { padding: 0 } }}
        >
          {navigation}
        </Drawer>
        <SystemBanners />
        <Layout.Header
          style={{
            background: token.colorBgContainer,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            paddingInline: 8,
            height: 56,
            lineHeight: 'normal',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          <Flex align="center" gap={8} style={{ height: '100%' }} wrap={false}>
            <Button
              type="text"
              icon={<MenuOutlined />}
              aria-label={ru.nav.menu}
              aria-expanded={drawerOpen}
              style={{ flex: 'none' }}
              onClick={() => {
                setDrawerOpen(true);
              }}
            />
            <Typography.Text strong ellipsis style={{ flex: 1, minWidth: 0, fontSize: 16 }}>
              {sectionLabel(items, selected) ?? (config.org_name || config.product_name)}
            </Typography.Text>
            <MobileUserMenu onAction={onAction} />
          </Flex>
        </Layout.Header>
        <Layout.Content style={{ padding: 12, minWidth: 0 }}>
          <Suspense fallback={<FullScreenSpin />}>
            <Outlet />
          </Suspense>
        </Layout.Content>
        <Layout.Footer style={{ padding: '8px 12px', textAlign: 'center' }}>
          <PoweredBy />
        </Layout.Footer>
        {modals}
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Layout.Sider
        width={250}
        breakpoint="lg"
        collapsedWidth={0}
        theme={mode}
        style={{ borderInlineEnd: `1px solid ${token.colorBorderSecondary}` }}
      >
        <Typography.Title level={5} style={{ margin: 16 }} ellipsis>
          {config.org_name || config.product_name}
        </Typography.Title>
        {navigation}
      </Layout.Sider>
      <Layout>
        <SystemBanners />
        <Layout.Header
          style={{
            background: token.colorBgContainer,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            paddingInline: 24,
          }}
        >
          <Flex align="center" justify="flex-end" gap={16} style={{ height: '100%' }}>
            <RoleSwitcher />
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  {
                    key: 'password',
                    label: ru.header.changePassword,
                    icon: <KeyOutlined />,
                  },
                  { key: 'sessions', label: ru.header.sessions, icon: <DesktopOutlined /> },
                  { key: 'logout', label: ru.header.logout, icon: <LogoutOutlined /> },
                ],
                onClick: ({ key }) => {
                  onAction(key as UserAction);
                },
              }}
            >
              <Button type="text" aria-label={ru.header.userMenu}>
                <Flex align="center" gap={8}>
                  <UserOutlined aria-hidden />
                  <Typography.Text strong>{displayName(user)}</Typography.Text>
                  <Tag color="blue">{ru.roles[user.role]}</Tag>
                  <DownOutlined aria-hidden />
                </Flex>
              </Button>
            </Dropdown>
            <Flex align="center" gap={8}>
              <Switch
                id="theme-switch"
                checked={mode === 'dark'}
                onChange={toggle}
                aria-label={ru.header.darkTheme}
                checkedChildren={<MoonOutlined />}
                unCheckedChildren={<SunOutlined />}
              />
              <label htmlFor="theme-switch">{ru.header.darkTheme}</label>
            </Flex>
          </Flex>
        </Layout.Header>
        <Layout.Content style={{ padding: 24, minWidth: 0 }}>
          <Suspense fallback={<FullScreenSpin />}>
            <Outlet />
          </Suspense>
        </Layout.Content>
        <Layout.Footer style={{ padding: '8px 24px', textAlign: 'center' }}>
          <PoweredBy />
        </Layout.Footer>
        {modals}
      </Layout>
    </Layout>
  );
}
